import { useEffect, useMemo, useRef, useState } from "react"
import Dialog from "./Dialog"
import { sanitizeUserTextInput } from "../utils/security"
import { ORGANIZATION_TYPES } from "../utils/organizationProfile"

export default function OrganizationDialog({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
  errorMessage = "",
}) {
  const inputRef = useRef(null)
  const [name, setName] = useState("")
  const [organizationType, setOrganizationType] = useState(
    ORGANIZATION_TYPES.AUTOMOTIVE,
  )
  const [validationError, setValidationError] = useState("")

  useEffect(() => {
    if (!isOpen) {
      setName("")
      setOrganizationType(ORGANIZATION_TYPES.AUTOMOTIVE)
      setValidationError("")
      return
    }

    const focusTimer = window.setTimeout(() => {
      inputRef.current?.focus()
    }, 50)

    return () => window.clearTimeout(focusTimer)
  }, [isOpen])

  const effectiveError = useMemo(
    () => validationError || errorMessage,
    [errorMessage, validationError]
  )

  const handleNameChange = (event) => {
    setName(event.target.value)
    if (validationError) {
      setValidationError("")
    }
  }

  const handleSubmit = (event) => {
    event.preventDefault()

    const { sanitized, error } = sanitizeUserTextInput(name, {
      maxLength: 120,
    })

    if (!sanitized) {
      setValidationError(error || "Invalid organization name.")
      return
    }

    onSubmit(sanitized, organizationType)
  }

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title="Create organization">
      <form className="organization-dialog__form" onSubmit={handleSubmit}>
        <label className="form-label" htmlFor="organization-name">
          Organization name
        </label>
        <input
          id="organization-name"
          ref={inputRef}
          type="text"
          value={name}
          onChange={handleNameChange}
          placeholder="e.g. Stellar Motors"
          autoComplete="organization"
        />
        <fieldset className="organization-dialog__type-options">
          <legend>Organization type</legend>
          <label>
            <input
              type="radio"
              name="organization-type"
              value={ORGANIZATION_TYPES.AUTOMOTIVE}
              checked={organizationType === ORGANIZATION_TYPES.AUTOMOTIVE}
              onChange={(event) => setOrganizationType(event.target.value)}
            />
            <span>
              <strong>Automotive</strong>
              <small>Includes automotive event audits and performance reporting.</small>
            </span>
          </label>
          <label>
            <input
              type="radio"
              name="organization-type"
              value={ORGANIZATION_TYPES.STANDARD}
              checked={organizationType === ORGANIZATION_TYPES.STANDARD}
              onChange={(event) => setOrganizationType(event.target.value)}
            />
            <span>
              <strong>Standard business</strong>
              <small>Uses configurable account audits without automotive-only rules.</small>
            </span>
          </label>
        </fieldset>
        {effectiveError && (
          <p className="dialog__error" role="alert">
            {effectiveError}
          </p>
        )}
        <div className="dialog__footer">
          <button
            type="button"
            className="button--secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Creating..." : "Create organization"}
          </button>
        </div>
      </form>
    </Dialog>
  )
}
